import {
  type NotificationsQuery,
  useNotificationsQuery,
} from "@graphql/notifications/Notifications.g";
import { NotificationEnum } from "@graphql/generated/graphql.g";
import { json2csv } from "json-2-csv";
import { useCallback, useMemo, useState } from "react";

import { queryClient } from "@providers/QueryClient/queryClient";
import { useTableColumnSettings } from "@providers/TableColumnSettingsProvider/useTableColumnSettings";

import {
  CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS,
  ClientNotificationTableColumns,
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
} from "../UI/ClientNotificationsTableColumns";

type Props = {
  tableId: string;
  clientId: string;
  exportType?: "csv";
  // Filter parameters - same as the table component
  notificationType?: NotificationEnum[];
  applicationNameSearch?: string;
};

export const useClientNotificationTableExport = ({
  tableId,
  clientId,
  exportType = "csv",
  notificationType,
  applicationNameSearch,
}: Props) => {
  const [isExporting, setIsExporting] = useState(false);
  const { getColumnSettings } = useTableColumnSettings({
    tableId,
  });

  const exportData = useCallback(async () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      const BATCH_SIZE = 100;
    const allNotifications: NonNullable<
      NotificationsQuery["notifications"]
    >["edges"] = [];
    let hasNextPage = true;
    let offset = 0;

    // Fetch data in batches of 100 records
    while (hasNextPage) {
      const data = await queryClient.fetchQuery({
        queryKey: useNotificationsQuery.getKey({
          clientId,
          first: BATCH_SIZE,
          offset: offset,
          resolvedAt_Isnull: true,
          notificationType_In:
            notificationType && notificationType.length > 0
              ? notificationType
              : undefined,
          application_Resource_ProductName_Icontains:
            applicationNameSearch && applicationNameSearch.trim()
              ? applicationNameSearch.trim()
              : undefined,
        }),
        queryFn: useNotificationsQuery.fetcher({
          clientId,
          first: BATCH_SIZE,
          offset: offset,
          resolvedAt_Isnull: true,
          notificationType_In:
            notificationType && notificationType.length > 0
              ? notificationType
              : undefined,
          application_Resource_ProductName_Icontains:
            applicationNameSearch && applicationNameSearch.trim()
              ? applicationNameSearch.trim()
              : undefined,
        }),
      });

      const edges = data.notifications?.edges || [];
      allNotifications.push(...edges);

      // Check if we have more data to fetch
      hasNextPage = edges.length === BATCH_SIZE;
      offset += BATCH_SIZE;
    }

    const columnSettings = getColumnSettings() ?? DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS;
    const selectedColumns = Object.entries(columnSettings)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value)
      .map(([key]) => key as ClientNotificationTableColumns);

    const notifications = allNotifications
      .map((edge) => {
        if (!edge?.node) return null;

        const notification: Record<string, unknown> = {};

        selectedColumns.forEach((column) => {
          const label = CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[column];

          switch (column) {
            case ClientNotificationTableColumns.applicationName:
              notification[label] = 
                edge.node?.application?.resource.productName || "Unknown Notification";
              break;
            case ClientNotificationTableColumns.notificationType:
              notification[label] = edge.node?.notificationType || "";
              break;
            case ClientNotificationTableColumns.createdAt:
              notification[label] = edge.node?.createdAt
                ? new Date(edge.node.createdAt).toLocaleDateString()
                : "";
              break;
            default:
              notification[label] = "";
          }
        });

        return notification;
      })
      .filter(
        (notification): notification is Record<string, unknown> =>
          notification !== null,
      );

    if (!notifications || notifications.length === 0) {
      throw new Error("No notifications found");
    }

      if (exportType === "csv") {
        const csv = json2csv(notifications);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `client-notifications-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export failed:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [
    exportType,
    getColumnSettings,
    clientId,
    notificationType,
    applicationNameSearch,
    isExporting,
  ]);

  return useMemo(() => ({ exportData, isExporting }), [exportData, isExporting]);
};